import { useState, useCallback, useEffect, useRef } from 'react';
import { clientPollingManager } from '../services/clientPolling.service';
import { versoesStatusService } from '../services/versoesStatus.service';

/**
 * Hook para gerenciar múltiplas versões em processamento simultaneamente
 * @param {Function} onRecarregarVersoes - Callback opcional para recarregar lista de versões
 */
export function useVersoesEmProcessamento(onRecarregarVersoes = null) {
  const [versoesEmProcessamento, setVersoesEmProcessamento] = useState({});
  const onRecarregarRef = useRef(onRecarregarVersoes);
  
  // Atualizar ref quando callback muda
  useEffect(() => {
    onRecarregarRef.current = onRecarregarVersoes;
  }, [onRecarregarVersoes]);

  /**
   * Inicia polling para uma versão
   */
  const iniciarPollingVersao = useCallback(async (versaoId, statusInicial = null) => {
    // Criar status inicial se não foi fornecido
    const statusInicialCompleto = statusInicial || versoesStatusService.criarStatusAguardando();

    // Adicionar versão ao estado
    setVersoesEmProcessamento(prev => ({
      ...prev,
      [versaoId]: {
        status: statusInicialCompleto,
        dadosProgresso: versoesStatusService.mapearStatusParaProgressData(statusInicialCompleto),
      },
    }));

    // Buscar status inicial se não foi fornecido
    if (!statusInicial) {
      try {
        // Aguardar um pouco para garantir que o status foi registrado no Redis
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const status = await versoesStatusService.buscarStatus(versaoId);
        if (status) {
          setVersoesEmProcessamento(prev => ({
            ...prev,
            [versaoId]: {
              status,
              dadosProgresso: versoesStatusService.mapearStatusParaProgressData(status),
            },
          }));
        }
      } catch (error) {
        // Erro silencioso - polling continuará tentando
      }
    }

    // Iniciar polling
    clientPollingManager.iniciarPolling(
      versaoId,
      // onProgress
      (status) => {
        setVersoesEmProcessamento(prev => ({
          ...prev,
          [versaoId]: {
            ...prev[versaoId],
            status,
            dadosProgresso: versoesStatusService.mapearStatusParaProgressData(status),
          },
        }));
      },
      // onComplete
      (status) => {
        setVersoesEmProcessamento(prev => ({
          ...prev,
          [versaoId]: {
            ...prev[versaoId],
            status: { ...status, concluido: true },
            dadosProgresso: {
              ...versoesStatusService.mapearStatusParaProgressData(status),
              sucesso: true,
            },
          },
        }));

        // Backend fará limpeza automática do Redis após delay (5 minutos)
        // Não precisamos chamar removerStatusVersao aqui

        // Remover após delay (3 segundos)
        setTimeout(() => {
          setVersoesEmProcessamento(prev => {
            const novo = { ...prev };
            delete novo[versaoId];
            return novo;
          });
          clientPollingManager.pararPolling(versaoId);
          
          // Recarregar versões se callback fornecido
          if (onRecarregarRef.current) {
            onRecarregarRef.current();
          }
        }, 3000);
      },
      // onError
      (error) => {
        setVersoesEmProcessamento(prev => ({
          ...prev,
          [versaoId]: {
            ...prev[versaoId],
            status: { erro: true, mensagem: error.message || "Erro ao processar" },
            dadosProgresso: {
              mensagem: error.message || "Erro ao processar",
              erro: true,
            },
          },
        }));

        // Backend fará limpeza automática do Redis após delay (5 minutos)
        // Não precisamos chamar removerStatusVersao aqui

        // Após delay, remover versão com erro
        setTimeout(() => {
          setVersoesEmProcessamento(prev => {
            const novo = { ...prev };
            delete novo[versaoId];
            return novo;
          });
          clientPollingManager.pararPolling(versaoId);
          
          // Recarregar versões se callback fornecido
          if (onRecarregarRef.current) {
            onRecarregarRef.current();
          }
        }, 60000); // Aguardar cleanup do backend (1 minuto)
      },
      1000 // interval em ms
    );
  }, []);

  /**
   * Para polling de uma versão específica
   */
  const pararPollingVersao = useCallback((versaoId) => {
    clientPollingManager.pararPolling(versaoId);
    setVersoesEmProcessamento(prev => {
      const novo = { ...prev };
      delete novo[versaoId];
      return novo;
    });
  }, []);

  /**
   * Para polling de todas as versões
   */
  const pararTodosPollings = useCallback(() => {
    setVersoesEmProcessamento(prev => {
      Object.keys(prev).forEach(versaoId => {
        clientPollingManager.pararPolling(Number(versaoId));
      });
      return {};
    });
  }, []);

  /**
   * Verifica e inicia polling para versões ativas
   */
  const verificarEIniciarPollingVersoes = useCallback(async (versoes) => {
    if (!versoes || versoes.length === 0) return;

    // Filtrar versões que já foram concluídas antes de verificar status
    const versoesNaoConcluidas = versoes.filter((versao) => {
      // Se tem scripts disponíveis, já foi concluída
      if (versoesStatusService.isVersaoConcluida(versao)) {
        return false;
      }
      return true;
    });

    if (versoesNaoConcluidas.length === 0) {
      // Todas as versões já foram concluídas, não precisa verificar status
      return;
    }

    const versoesAtivas = await versoesStatusService.verificarStatusVersoes(versoesNaoConcluidas);

    versoesAtivas.forEach(({ versaoId, status }) => {
      iniciarPollingVersao(versaoId, status);
    });
  }, [iniciarPollingVersao]);

  /**
   * Cleanup ao desmontar
   */
  useEffect(() => {
    return () => {
      setVersoesEmProcessamento(prev => {
        Object.keys(prev).forEach(versaoId => {
          clientPollingManager.pararPolling(Number(versaoId));
        });
        return {};
      });
    };
  }, []);

  return {
    versoesEmProcessamento,
    iniciarPollingVersao,
    pararPollingVersao,
    pararTodosPollings,
    verificarEIniciarPollingVersoes,
  };
}

